import Modal from '@/components/Modal/Modal';
import Button from '@/components/Button/Button';
import { FormattedMessage } from 'react-intl';
import SaveIcon from '@/assets/save-floppy-disk.svg?react';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { TableData } from '@/components/Table/TableHelpers';
import UploadPhase from './UploadPhase';
import PreviewPhase from './PreviewPhase';
import { parseContactsCsv } from '@/pages/PartnerAdminPage/PartnerAdminPageHelpers';
import { ContactSchema, PermissionSchema } from '@/lib/types';
import { useQuery } from '@tanstack/react-query';
import { fetchFilters, postBatchInvite } from '@/lib/api';
import { getPermissionsByKeys } from '@/lib/permissions';
import { RowErrors } from '@/components/Table/TableHelpers';
import { getPermissionIdsByKeys } from '@/lib/permissions';
import { AxiosError } from 'axios';
import { ApiError } from '@/lib/types';
import useApiErrorHandler from '@/hooks/useApiErrorHandler';

type Phase = 'upload' | 'preview';

interface ContactsImportModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (contacts: ContactSchema[]) => void;
  partnerId?: number;
}

export interface ImportResult {
  success: number;
  failed: number;
  total: number;
}

const ContactsImportModal = ({
  open,
  onClose,
  onSave,
  partnerId,
}: ContactsImportModalProps) => {
  const [phase, setPhase] = useState<Phase>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [viewComplianceDocs, setViewComplianceDocs] = useState(true);
  const [editContacts, setEditContacts] = useState(false);
  const [editedContacts, setEditedContacts] = useState<
    ContactSchema[]
  >([]);
  const [errors, setErrors] = useState<RowErrors<ContactSchema>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] =
    useState<ImportResult | null>(null);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);

  const { data } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });
  const { handleError: handleApiError } = useApiErrorHandler();

  useEffect(() => {
    if (!open) {
      setPhase('upload');
      setFile(null);
      setViewComplianceDocs(true);
      setEditContacts(false);
      setEditedContacts([]);
      setErrors({});
      setIsImporting(false);
      setImportResult(null);
      setGeneralErrors([]);
    }
  }, [open]);

  const handlePreview = () => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setGeneralErrors([
        'Invalid file type. Please upload a CSV file (.csv)',
      ]);
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setGeneralErrors([
        'File size exceeds 5MB. Please upload a smaller file.',
      ]);
      return;
    }

    setGeneralErrors([]);

    const permissions = getPermissionsByKeys(
      data?.permissions || [],
      [
        ...(viewComplianceDocs
          ? [
              'markets.read',
              'games.read',
              'compliance_documents.read',
            ]
          : []),
        ...(editContacts
          ? [
              'markets.read',
              'games.read',
              'partners.read',
              'users.create',
              'users.read',
              'users.update',
              'users.delete',
              'users.manage_roles',
              'users.manage_permissions',
            ]
          : []),
      ],
    );

    const reader = new FileReader();
    reader.onerror = () => {
      setGeneralErrors([
        'Failed to read file. Please ensure the file is not corrupted.',
      ]);
    };
    reader.onload = (event) => {
      try {
        const csvData = event.target?.result?.toString();
        if (!csvData) {
          setGeneralErrors(['File is empty or could not be read.']);
          return;
        }

        const parsedData = parseContactsCsv(csvData);

        if (!parsedData || parsedData.length === 0) {
          setGeneralErrors([
            'No valid contacts found in the file. Please check the file format.',
          ]);
          return;
        }

        const contactsWithPermissions = parsedData.map((contact) => ({
          id: contact.id,
          name: contact.name,
          email: contact.email,
          permissions,
          roles: [],
          viewReports: false,
          viewComplianceDocs: viewComplianceDocs,
          editContacts: editContacts,
          partners: [],
        }));
        setEditedContacts(contactsWithPermissions);
        setPhase('preview');
        setGeneralErrors([]);
      } catch (error) {
        setGeneralErrors([
          'Failed to parse CSV file. Please ensure it follows the correct format.',
        ]);
        console.error('CSV parsing error:', error);
      }
    };
    reader.readAsText(file);
  };

  const handlePermissionsChange = (
    id: number,
    names: string[],
    value: TableData,
  ) => {
    const includePermission = (
      permissions: PermissionSchema[],
      permission: PermissionSchema,
    ) => {
      return permissions.some((p) => p.id === permission.id);
    };
    const togglePermissionKey = (
      basePermissions: PermissionSchema[],
      togglePermissions: PermissionSchema[],
    ) => {
      if (value) {
        return [
          ...basePermissions,
          ...togglePermissions.filter(
            (p) => !includePermission(basePermissions, p),
          ),
        ];
      }
      return basePermissions.filter(
        (p) => !includePermission(togglePermissions, p),
      );
    };

    const permissions = getPermissionsByKeys(
      data?.permissions || [],
      names,
    );

    setEditedContacts((prev) =>
      prev.map((contact) =>
        contact.id === id
          ? {
              ...contact,
              permissions: togglePermissionKey(
                contact.permissions,
                permissions,
              ),
            }
          : contact,
      ),
    );
  };

  const handleDelete = (id: number) => {
    setEditedContacts((prev) =>
      prev.filter((contact) => contact.id !== id),
    );
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange =
    (prop: 'email' | 'name') => (id: number, value: TableData) => {
      if (errors[id]?.[prop]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          if (newErrors[id]) {
            const { [prop]: _, ...rest } = newErrors[id];
            if (Object.keys(rest).length === 0) {
              delete newErrors[id];
            } else {
              newErrors[id] = rest;
            }
          }
          return newErrors;
        });
      }

      setEditedContacts((prev) =>
        prev.map((contact) =>
          contact.id === id
            ? { ...contact, [prop]: value as string }
            : contact,
        ),
      );
    };

  const handleSave = async () => {
    const newErrors: RowErrors<ContactSchema> = {};

    editedContacts.forEach((contact) => {
      const contactErrors: Partial<
        Record<keyof ContactSchema, string>
      > = {};

      if (!contact.name?.trim()) {
        contactErrors.name = 'Contact Name is required';
      }

      if (!contact.email?.trim()) {
        contactErrors.email = 'Contact Email is required';
      } else if (!validateEmail(contact.email)) {
        contactErrors.email = 'Invalid email format';
      }

      if (Object.keys(contactErrors).length > 0) {
        newErrors[contact.id] = contactErrors;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!partnerId) {
      console.error('Partner ID is required');
      return;
    }

    setErrors({});
    setIsImporting(true);
    setImportResult(null);

    try {
      const invitations = editedContacts.map((contact) => {
        const permissionIds = getPermissionIdsByKeys(
          data?.permissions || [],
          contact.permissions.map((p) => p.key),
        );

        return {
          name: contact.name,
          email: contact.email,
          partner_id: partnerId,
          permission_ids: permissionIds,
        };
      });

      const results = await postBatchInvite({ invitations });

      setImportResult({
        success: results.data.successful,
        failed: results.data.failed,
        total: results.data.total,
      });

      const apiErrors: RowErrors<ContactSchema> = {};
      results.data.failures.forEach((failure) => {
        const failedContact = editedContacts.find(
          (c) => c.email === failure.email,
        );
        if (failedContact) {
          apiErrors[failedContact.id] = {
            _row: failure.error,
          };
        }
      });
      setErrors(apiErrors);

      setEditedContacts(
        editedContacts.filter(
          (contact) =>
            !results.data.successes.some(
              (success) => success.email === contact.email,
            ),
        ),
      );

      if (results.data.failed === 0) {
        if (onSave) {
          onSave(editedContacts);
        }
      }
    } catch (error) {
      const {
        generalErrors: apiGeneralErrors,
        fieldErrors,
        unhandledErrors,
      } = handleApiError(error as AxiosError<ApiError>);

      const newGeneralErrors = [
        ...apiGeneralErrors,
        ...unhandledErrors,
      ];
      if (fieldErrors?.invitations) {
        newGeneralErrors.push(fieldErrors?.invitations);
      }
      setGeneralErrors(newGeneralErrors);

      if (Object.keys(fieldErrors).length > 0) {
        const apiErrors: RowErrors<ContactSchema> = {};
        editedContacts.forEach((contact, index) => {
          const contactErrors: Partial<
            Record<keyof ContactSchema, string>
          > = {};
          if (fieldErrors[`invitations.${index}.name`]) {
            contactErrors.name =
              fieldErrors[`invitations.${index}.name`];
          }
          if (fieldErrors[`invitations.${index}.email`]) {
            contactErrors.email =
              fieldErrors[`invitations.${index}.email`];
          }
          if (Object.keys(contactErrors).length > 0) {
            apiErrors[contact.id] = contactErrors;
          }
        });
        if (Object.keys(apiErrors).length > 0) {
          setErrors(apiErrors);
        }
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleBack = () => {
    setPhase('upload');
  };

  const handleClose = () => {
    onClose();
  };

  const getActions = () => {
    if (phase === 'upload') {
      return [
        <Button
          key="close"
          variant="text"
          onClick={handleClose}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Button
          key="preview"
          variant="contained"
          onClick={handlePreview}
          disabled={!file}
          aria-label="Preview Button"
        >
          <FormattedMessage id="preview" defaultMessage="Preview" />
        </Button>,
      ];
    }

    return [
      <Button
        key="back"
        variant="text"
        onClick={handleBack}
        disabled={isImporting}
        aria-label="Back Button"
      >
        <FormattedMessage id="back" defaultMessage="Back" />
      </Button>,
      <Button
        key="save"
        variant="contained"
        disabled={isImporting || editedContacts.length === 0}
        loading={isImporting}
        onClick={handleSave}
        aria-label="Save Button"
      >
        <StyledSaveIcon disabled={isImporting} />
        <FormattedMessage id="save" defaultMessage="Save" />
      </Button>,
    ];
  };

  return (
    <StyledModal
      open={open}
      onClose={handleClose}
      title={
        phase === 'upload'
          ? 'Upload Contacts File'
          : 'Preview Contacts'
      }
      actions={getActions()}
      generalErrors={generalErrors}
    >
      {phase === 'upload' ? (
        <UploadPhase
          file={file}
          onFileChange={(newFile) => {
            setFile(newFile);
            setGeneralErrors([]);
          }}
          viewComplianceDocs={viewComplianceDocs}
          onViewComplianceDocsChange={setViewComplianceDocs}
          editContacts={editContacts}
          onEditContactsChange={setEditContacts}
          error={generalErrors.length > 0 ? generalErrors[0] : null}
        />
      ) : (
        <PreviewPhase
          contacts={editedContacts}
          onPermissionChange={handlePermissionsChange}
          onDelete={handleDelete}
          onEmailChange={handleInputChange('email')}
          onNameChange={handleInputChange('name')}
          errors={errors}
          loading={isImporting}
          importResult={importResult}
        />
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)(() => ({
  width: '90%',
  maxWidth: '1100px',
}));

const StyledSaveIcon = styled(SaveIcon)<{ disabled: boolean }>(
  ({ theme, disabled }) => ({
    path: {
      transition: 'all 0.2s ease-in-out',
      stroke: disabled
        ? theme.palette.text.disabled
        : theme.palette.white,
    },
  }),
);

export default ContactsImportModal;
